<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import CloseIcon from '@mobile/components/Icons/CloseIcon.vue';
import InputError from '@mobile/components/Form/InputError.vue';
import Loader from '@mobile/components/Loader.vue';
import issueService from '@mobile/services/issue';
import InputControl from '@mobile/components/Form/InputControl.vue';
import BaseView from '@mobile/components/BaseView.vue';
import SaveIcon from '@mobile/components/Icons/SaveIcon.vue';
import AutosizeTextarea from '@/components/Form/AutosizeTextarea.vue';
import TitleBar from '@/components/TitleBar.vue';
import CheckboxGroup from '@/components/CheckboxGroup.vue';
import InputControlDate from '@/components/Form/InputControlDate.vue';
import {
    ISSUE_PRIORITY,
    ISSUE_PERMISSIONS,
    ISSUE_STATUS,
    ISSUE_PAYLOAD_TYPE,
} from '@/constants';
import DropdownModal from '@/views/ConductInspection/Modals/DropdownModal.vue';
import Payload from '@/views/Issues/Payloads/Payload.vue';
import AddPhotos from '@/components/AddPhotos.vue';

export default {
    name: 'EditIssue',

    components: {
        AddPhotos,
        CloseIcon,
        InputError,
        DropdownModal,
        Loader,
        InputControlDate,
        CheckboxGroup,
        InputControl,
        AutosizeTextarea,
        BaseView,
        TitleBar,
        SaveIcon,
        Payload,
    },

    props: {
        id: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        showAssigneeModal: false,
        showPriorityModal: false,
        initialDeadline: null,
        form: {
            title: '',
            description: '',
            site: null,
            assignees: [],
            priority: null,
            deadline: null,
            suggestedCorrectiveAction: '',
            status: null,
            performedCorrectiveAction: '',
            photos: [],
        },
        payloadData: {},
    }),

    validations: {
        form: {
            title: { required },
            priority: { required },
        },
    },

    computed: {
        ...mapGetters({
            filterOptions: 'issues/filterOptions',
            isLoading: 'issues/isLoading',
            isInitialLoading: 'issues/isInitialLoading',
            hasPermission: 'authentication/hasPermission',
        }),

        priorities() {
            return {
                [ISSUE_PRIORITY.MEDIUM]: this.$t('Normal'),
                [ISSUE_PRIORITY.HIGH]: this.$t('High'),
            };
        },

        assignees() {
            const encodeAssignees = (assignees) => Object.keys(assignees).map((key) => ({
                uuid: key,
                title: assignees[key],
            }));

            return encodeAssignees(this.filterOptions.assignees);
        },

        selectedAssignees() {
            return this.form.assignees.map(String);
        },

        canCreateIssueWithAssignee() {
            return this.hasPermission(
                ISSUE_PERMISSIONS.CREATE_ISSUE_WITH_ASSIGNEE,
            );
        },

        hasMoreThanOneSite() {
            return (
                this.filterOptions.sites
                && Object.keys(this.filterOptions.sites).length > 1
            );
        },

        priorityOptions() {
            return [
                {
                    key: ISSUE_PRIORITY.MEDIUM,
                    value: this.$t('Normal'),
                    status: 'bg-yellow',
                },
                {
                    key: ISSUE_PRIORITY.HIGH,
                    value: this.$t('High'),
                    status: 'bg-red',
                },
            ];
        },

        statusOptions() {
            return [
                {
                    key: ISSUE_STATUS.OPEN,
                    value: this.$t('ToDo'),
                    status: 'bg-blue',
                },
                {
                    key: ISSUE_STATUS.IN_PROGRESS,
                    value: this.$t('Started'),
                    status: 'bg-yellow',
                },
                {
                    key: ISSUE_STATUS.SOLVED,
                    value: this.$t('Done'),
                    status: 'bg-green',
                },
                {
                    key: ISSUE_STATUS.CANT_DO,
                    value: this.$t('Can\'t do'),
                    status: 'bg-60',
                },
            ];
        },

        isSolvedIssue() {
            // eslint-disable-next-line eqeqeq
            return this.form.status == ISSUE_STATUS.SOLVED;
        },

        hasPayload() {
            return this.payloadData && this.payloadData.payload;
        },

        isSensorAlarmPayload() {
            return (
                this.hasPayload
                && this.payloadData.payload.type
                && this.payloadData.payload.type
                    === ISSUE_PAYLOAD_TYPE.SENSOR_ALARM
            );
        },
    },

    async created() {
        try {
            const [
                {
                    title,
                    description,
                    siteId,
                    priority,
                    dueAt,
                    suggestedCorrectiveAction,
                    assignees,
                    status,
                    performedCorrectiveAction,
                    sensorsLastMeasurement,
                    payload,
                    photos,
                },
            ] = await Promise.all([
                issueService.get(this.id),
                this.editIssuesScreenCreated(),
            ]);

            this.form = {
                ...this.form,
                title,
                description,
                descriptionReadOnly: Boolean(payload && payload.sensor_id),
                site: siteId,
                assignees: assignees ? Object.keys(assignees) : [],
                priority,
                deadline: dueAt || null,
                suggestedCorrectiveAction,
                status,
                performedCorrectiveAction,
                photos,
            };
            this.initialDeadline = dueAt || null;

            if (sensorsLastMeasurement) {
                this.payloadData.sensorsLastMeasurement = sensorsLastMeasurement;
            }

            if (payload) {
                this.$set(this.payloadData, 'payload', payload);
            }
        } catch (error) {
            this.$toasted.show(this.$t('Failed to fetch issue information'), {
                type: 'error',
            });
        }
    },

    methods: {
        ...mapActions({
            editIssuesScreenCreated: 'issues/editIssuesScreenCreated',
            issueEdited: 'issues/issueEdited',
        }),

        formSubmitted() {
            if (!this.hasMoreThanOneSite && this.filterOptions.sites) {
                const [firstSite] = Object.keys(this.filterOptions.sites);
                this.form.site = Number(firstSite);
            }

            this.$v.$touch();

            if (this.$v.$invalid) {
                this.$toasted.show(
                    this.$t('This page has form errors, please fix them'),
                    { type: 'error' },
                );
            } else {
                const payload = { id: this.id, form: this.form };
                this.issueEdited(payload)
                    .then(() => {
                        this.$toasted.show(this.$t('Issue edited'), {
                            type: 'success',
                        });
                        this.$router.go(-1);
                    })
                    .catch(() => {
                        this.$toasted.show(
                            this.$t(
                                "We couldn't save your issue, please try again later",
                            ),
                            { type: 'error' },
                        );
                    });
            }
        },

        selectAssigneeClicked() {
            this.showAssigneeModal = true;
        },

        assigneeModalSaved(assignees) {
            this.form.assignees = assignees.map(Number);
            this.assigneeModalClosed();
        },

        assigneeModalClosed() {
            this.showAssigneeModal = false;
        },

        selectPriorityClicked() {
            this.showPriorityModal = true;
        },

        priorityModalSelected(priority) {
            this.form.priority = priority;
            this.priorityModalClosed();
        },

        priorityModalClosed() {
            this.showPriorityModal = false;
        },
    },
};
</script>

<template>
    <BaseView contentClass="bg-white">
        <template v-slot:top>
            <TitleBar
                :title="$t('Update Issue')"
                @left-click="$router.go(-1)"
                @right-click="formSubmitted"
            >
                <template v-slot:leftIcon>
                    <CloseIcon />
                </template>

                <template v-slot:rightIcon>
                    <SaveIcon />
                </template>
            </TitleBar>
        </template>

        <div v-if="isInitialLoading" class="middle-of-page flex-col text-80">
            <Loader />
        </div>

        <form
            v-show="!isInitialLoading"
            class="raise-issue"
            @submit.prevent="formSubmitted"
            novalidate
        >
            <InputControl :label="$t('Title')">
                <input
                    class="form-input form-control form-input-bordered w-full"
                    id="title"
                    v-model.trim.lazy="$v.form.title.$model"
                    name="title"
                    type="text"
                    :placeholder="$t('Enter title')"
                />
                <div v-if="$v.form.title.$anyError">
                    <InputError
                        v-if="$v.form.title.$dirty"
                        :condition="!$v.form.title.required"
                    >
                        {{
                            $t("The {field} field is required", {
                                field: $t("title")
                            })
                        }}
                    </InputError>
                </div>
            </InputControl>

            <Payload
                v-if="hasPayload && isSensorAlarmPayload"
                :issue="payloadData"
                class="mb-6"
            />

            <InputControl
                v-if="!form.descriptionReadOnly && !isSensorAlarmPayload"
                :label="$t('Description')"
            >
                <AutosizeTextarea
                    v-model.trim.lazy="form.description"
                    :placeholder="$t('Enter description (optional)')"
                />
            </InputControl>

            <InputControl>
                <AddPhotos v-model="form.photos" />
            </InputControl>

            <InputControl :label="$t('Status')">
                <CheckboxGroup v-model="form.status" :options="statusOptions" />
            </InputControl>

            <InputControl
                v-show="isSolvedIssue"
                :label="$t('Performed corrective action')"
            >
                <AutosizeTextarea
                    v-model.trim.lazy="form.performedCorrectiveAction"
                    :placeholder="
                        $t('Enter performed corrective action (optional)')
                    "
                />
            </InputControl>

            <InputControl v-show="hasMoreThanOneSite" :label="$t('Site')">
                <select
                    v-model="form.site"
                    class="form-select form-input form-control form-input-bordered w-full"
                    :class="{
                        empty: !form.site
                    }"
                >
                    <option :value="null">{{
                        $t("Select site (optional)")
                    }}</option>
                    <option
                        v-for="(name, id) in filterOptions.sites"
                        :key="id"
                        :value="parseInt(id)"
                        >{{ name }}</option
                    >
                </select>
            </InputControl>

            <InputControl
                v-show="canCreateIssueWithAssignee"
                :label="$t('Assignee')"
            >
                <div
                    class="form-select form-input form-control form-input-bordered w-full"
                    :class="{
                        empty:
                            form.assignees === null ||
                            form.assignees.length === 0
                    }"
                    @click="selectAssigneeClicked"
                >
                    <span
                        v-if="
                            form.assignees === null ||
                                form.assignees.length === 0
                        "
                        >{{ $t("Select assignee (optional)") }}</span
                    >
                    <span v-else>{{
                        form.assignees
                            .map(id => this.filterOptions.assignees[id])
                            .join(", ")
                    }}</span>
                </div>
            </InputControl>

            <InputControl :label="$t('Priority')">
                <CheckboxGroup
                    v-model="form.priority"
                    :options="priorityOptions"
                />
            </InputControl>

            <InputControl
                v-if="canCreateIssueWithAssignee"
                :label="$t('Due at')"
            >
                <InputControlDate
                    v-model="form.deadline"
                    :initial-value="initialDeadline"
                    :class="{
                        empty: form.deadline === null
                    }"
                    :placeholder="$t('Select deadline (optional)')"
                    display-time
                    wrap-size
                />
            </InputControl>

            <InputControl :label="$t('Suggested corrective action')">
                <AutosizeTextarea
                    v-model.trim.lazy="form.suggestedCorrectiveAction"
                    :placeholder="
                        $t('Enter sugested corrective action (optional)')
                    "
                />
            </InputControl>

            <Payload
                v-if="hasPayload && !isSensorAlarmPayload"
                :issue="payloadData"
                class="mb-6"
            />

            <button
                class="btn btn-default btn-primary main-button"
                type="submit"
            >
                <span v-show="!isLoading">{{ $t("Save") }}</span>
                <Loader v-show="isLoading" width="20" />
            </button>
        </form>

        <portal to="modals">
            <transition name="fade">
                <DropdownModal
                    v-if="showAssigneeModal"
                    :responses="assignees"
                    :response="selectedAssignees"
                    :multiple="true"
                    @save="assigneeModalSaved"
                    @close="assigneeModalClosed"
                />
            </transition>
        </portal>
    </BaseView>
</template>
